<script setup lang="ts">
import type { Locale } from '@/settings/langs'
import { switchLocale } from '@/compositions/useLocale'
import { defaultLocale, languageSelectorEnabled, localeDisplayData, supportedLocales } from '@/settings/langs'

const props = withDefaults(defineProps<{ usePopover?: boolean }>(), { usePopover: false })

const { isDesktop } = tools
const route = useRoute()
const hasLocaleRoutes = computed(() => !!route?.meta?.pageData?.routerGroup)
const currentLocale = computed(() =>
  String(route?.meta?.pageData?.locale || route?.params?.locale || defaultLocale)
)

const sortedLocales = computed(() =>
  [...supportedLocales].sort((a, b) =>
    localeDisplayData[a as Locale].nativeName.localeCompare(
      localeDisplayData[b as Locale].nativeName
    )
  )
)

const columnCount = computed(() => props.usePopover ? 2 : Math.ceil(sortedLocales.value.length / 7))
const popoverWidth = computed(() => columnCount.value * 145)
</script>

<template>
  <template v-if="languageSelectorEnabled && hasLocaleRoutes">
    <!-- Desktop: compact trigger + popover list -->
    <el-popover
      v-if="isDesktop || props.usePopover"
      :show-arrow="false"
      :width="popoverWidth"
      popper-class="p-0 rounded-xl text-[18px]"
      :placement="props.usePopover ? 'bottom-start' : 'bottom-end'"
    >
      <template #reference>
        <a class="block cursor-pointer py-1 border-0 normal-case whitespace-nowrap select-none">
          🌐 {{ currentLocale.toUpperCase() }}
          <icon-ify
            icon="mingcute:down-fill"
            class="inline"
          />
        </a>
      </template>
      <ul
        class="grid gap-x-2"
        :style="{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }"
      >
        <li
          v-for="locale in sortedLocales"
          :key="locale"
          class="cursor-pointer px-3 py-0.75 text-left border-b font-bold last:border-b-0 whitespace-nowrap"
          :class="{ 'router-link-active': locale === currentLocale }"
          @click="switchLocale(locale as Locale)"
        >
          <span class="block py-1 w-full normal-case">
            {{ localeDisplayData[locale as Locale]?.flag }}
            {{ localeDisplayData[locale as Locale]?.nativeName }}
          </span>
        </li>
      </ul>
    </el-popover>

    <!-- Mobile: inline horizontal pills rendered inside the drawer -->
    <div
      v-else-if="!props.usePopover"
      class="grid gap-3 py-5"
      :style="{
        gridTemplateColumns: `repeat(${Math.min(sortedLocales.length, 5)}, auto)`,
        justifyContent: sortedLocales.length < 5 ? 'center' : 'start'
      }"
    >
      <button
        v-for="locale in sortedLocales"
        :key="locale"
        class="px-3 py-1 rounded-lg font-black text-sm uppercase transition-opacity"
        :class="locale === currentLocale
          ? 'bg-white text-gs-loyalty'
          : 'text-white opacity-60 hover:opacity-100'"
        @click="switchLocale(locale as Locale)"
      >
        {{ localeDisplayData[locale as Locale]?.flag }} {{ locale.toUpperCase() }}
      </button>
    </div>
  </template>
</template>
